import { trackDesignSystemAction } from 'analytics';
import type { KeyboardEvent } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Icon, { IconType } from 'components/Icon';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import DsListItem from '../DsListItem';
import * as S from './styles';

type NavEntry =
	| { kind: 'designSystem'; ds: DesignSystem }
	| { kind: 'noDesignSystem' };

interface LinkPanelProps {
	designSystems: DesignSystem[];
	// True when the parent dropdown is open and interactive (matches DsPicker Dropdown isOpen).
	isDropdownOpen: boolean;
	hasActiveDs?: boolean;
	onCreateNew: () => void;
	onLinkDs: (designSystem: DesignSystem | null) => Promise<void>;
	onBrowseShowcase?: () => void;
}

const LinkPanel = ({
	designSystems,
	isDropdownOpen,
	hasActiveDs = false,
	onCreateNew,
	onLinkDs,
	onBrowseShowcase,
}: LinkPanelProps) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const rowRefs = useRef<(HTMLElement | null)[]>([]);
	const highlightedIndexRef = useRef(-1);
	highlightedIndexRef.current = highlightedIndex;
	const { isMobile } = useSelector((r: RootReducer) => {
		return {
			isMobile: selectIsMobile(r.mode),
		};
	}, shallowEqual);

	// Sort design systems by updatedAt descending (same as sidebar)
	const sortedDesignSystems = useMemo(() => {
		return [...designSystems].sort(
			(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
		);
	}, [designSystems]);

	// Filter design systems based on search query
	const filteredDesignSystems = useMemo(() => {
		if (!searchQuery.trim()) return sortedDesignSystems;
		return sortedDesignSystems.filter((ds) =>
			ds.title.toLowerCase().includes(searchQuery.toLowerCase())
		);
	}, [sortedDesignSystems, searchQuery]);

	// Calculate recent design systems (first 2, or all if < 5)
	const recentDesignSystems = useMemo(() => {
		if (sortedDesignSystems.length < 5) {
			return sortedDesignSystems;
		}
		return sortedDesignSystems.slice(0, 2);
	}, [sortedDesignSystems]);

	// Calculate all design systems (filtered, excluding recent ones)
	const allDesignSystems = useMemo(() => {
		return filteredDesignSystems.filter((ds) => !recentDesignSystems.some((r) => r.id === ds.id));
	}, [filteredDesignSystems, recentDesignSystems]);

	const hasSearchQuery = searchQuery.trim().length > 0;

	// Flat list matching DOM order (Create / showcase rows are not included).
	const navigableEntries = useMemo((): NavEntry[] => {
		if (hasSearchQuery) {
			return filteredDesignSystems.map((ds) => ({ kind: 'designSystem' as const, ds }));
		}
		const entries: NavEntry[] = [];
		if (hasActiveDs) {
			entries.push({ kind: 'noDesignSystem' });
		}
		recentDesignSystems.forEach((ds) => entries.push({ kind: 'designSystem', ds }));
		allDesignSystems.forEach((ds) => entries.push({ kind: 'designSystem', ds }));
		return entries;
	}, [hasSearchQuery, filteredDesignSystems, hasActiveDs, recentDesignSystems, allDesignSystems]);

	const navigableKey = useMemo(
		() => navigableEntries.map((e) => (e.kind === 'designSystem' ? e.ds.id : 'no-ds')).join(','),
		[navigableEntries]
	);

	const handleClearSearch = () => {
		setSearchQuery('');
	};

	const showSearch = sortedDesignSystems.length >= 5;
	const showRecent = sortedDesignSystems.length > 0 && !hasSearchQuery;

	// Focus when the dropdown opens: LinkPanel can mount while the menu is closed (hidden but still
	// rendered), so focusing only on showSearch is too early; wait until isDropdownOpen is true.
	useEffect(() => {
		if (!showSearch || !isDropdownOpen) return;
		const id = requestAnimationFrame(() => {
			searchInputRef.current?.focus({ preventScroll: true });
		});
		return () => cancelAnimationFrame(id);
	}, [showSearch, isDropdownOpen]);
	const showAllSection = sortedDesignSystems.length >= 5 && !hasSearchQuery;
	const showSearchResults = hasSearchQuery;
	const hasNoDesignSystems = sortedDesignSystems.length === 0;

	useEffect(() => {
		setHighlightedIndex(-1);
	}, [navigableKey]);

	useLayoutEffect(() => {
		if (highlightedIndex < 0) return;
		rowRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
	}, [highlightedIndex]);

	const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		const n = navigableEntries.length;
		if (n === 0) return;
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setHighlightedIndex((i) => (i < 0 ? 0 : Math.min(i + 1, n - 1)));
			return;
		}
		if (e.key === 'ArrowUp') {
			e.preventDefault();
			setHighlightedIndex((i) => (i <= 0 ? -1 : i - 1));
			return;
		}
		if (e.key === 'Enter') {
			const idx = highlightedIndexRef.current;
			if (idx < 0) return;
			e.preventDefault();
			const entry = navigableEntries[idx];
			if (entry.kind === 'noDesignSystem') {
				void onLinkDs(null);
			} else {
				void onLinkDs(entry.ds);
			}
		}
	};

	let navIndex = 0;

	return (
		<>
			{!isMobile && (
				<S.DropdownSection>
					<S.CreateButton onClick={onCreateNew}>New Design System</S.CreateButton>
				</S.DropdownSection>
			)}

			{isMobile && hasNoDesignSystems && (
				<S.MobileEmptyState>
					No design systems yet. Create them on desktop to get started.
				</S.MobileEmptyState>
			)}

			{showSearch && (
				<>
					<S.SearchContainer>
						<S.SearchIcon>
							<Icon type={IconType.SEARCH} width={16} height={16} />
						</S.SearchIcon>
						<S.SearchInput
							ref={searchInputRef}
							type="text"
							placeholder="Search design systems..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							onKeyDown={onSearchKeyDown}
							isMobile={isMobile}
						/>
						{searchQuery && (
							<S.ClearButton onClick={handleClearSearch} aria-label="Clear search">
								<Icon type={IconType.CLOSE} width={14} height={14} />
							</S.ClearButton>
						)}
					</S.SearchContainer>
				</>
			)}

			{showSearchResults && (
				<>
					<S.SectionHeader>SEARCH RESULTS ({filteredDesignSystems.length})</S.SectionHeader>
					<S.ScrollArea>
						{filteredDesignSystems.length === 0 ? (
							<S.EmptyState>No design systems found</S.EmptyState>
						) : (
							filteredDesignSystems.map((ds) => {
								const idx = navIndex++;
								return (
									<DsListItem
										key={ds.id}
										ref={(el) => {
											rowRefs.current[idx] = el;
										}}
										item={ds}
										highlighted={highlightedIndex === idx}
										onClick={() => onLinkDs(ds)}
										onRowMouseEnter={() => setHighlightedIndex(idx)}
									/>
								);
							})
						)}
					</S.ScrollArea>
				</>
			)}

			{hasActiveDs && !hasSearchQuery && (() => {
				const idx = navIndex++;
				return (
					<S.NoDsRow
						type="button"
						ref={(el) => {
							rowRefs.current[idx] = el;
						}}
						$highlighted={highlightedIndex === idx}
						onClick={() => onLinkDs(null)}
						onMouseEnter={() => setHighlightedIndex(idx)}
					>
						No Design System
					</S.NoDsRow>
				);
			})()}

			{showRecent && (
				<>
					<S.SectionHeader>RECENT</S.SectionHeader>
					{recentDesignSystems.map((ds) => {
						const idx = navIndex++;
						return (
							<DsListItem
								key={ds.id}
								ref={(el) => {
									rowRefs.current[idx] = el;
								}}
								item={ds}
								highlighted={highlightedIndex === idx}
								onClick={() => onLinkDs(ds)}
								onRowMouseEnter={() => setHighlightedIndex(idx)}
							/>
						);
					})}
				</>
			)}

			{showAllSection && (
				<>
					<S.SectionHeader>ALL DESIGN SYSTEMS ({allDesignSystems.length})</S.SectionHeader>
					<S.ScrollArea>
						{allDesignSystems.length === 0 ? (
							<S.EmptyState>No design systems found</S.EmptyState>
						) : (
							allDesignSystems.map((ds) => {
								const idx = navIndex++;
								return (
									<DsListItem
										key={ds.id}
										ref={(el) => {
											rowRefs.current[idx] = el;
										}}
										item={ds}
										highlighted={highlightedIndex === idx}
										onClick={() => onLinkDs(ds)}
										onRowMouseEnter={() => setHighlightedIndex(idx)}
									/>
								);
							})
						)}
					</S.ScrollArea>
				</>
			)}

			{onBrowseShowcase && !isMobile && (
				<>
					<S.ShowcaseDivider />
					<S.BrowseShowcaseButton
						onClick={() => {
							trackDesignSystemAction('open_gallery');
							onBrowseShowcase();
						}}
					>
						<span>Browse gallery</span>
						<S.BrowseShowcaseArrow>→</S.BrowseShowcaseArrow>
					</S.BrowseShowcaseButton>
				</>
			)}
		</>
	);
};

export default LinkPanel;
